// apps/web/src/lib/compras/estorno.ts — Fase 13, Plano 24 (EST-10, COMP-03, D-11, D-12; UI-SPEC § Conferência pós-recebimento).
// Textos e validação PURA do estorno da entrada e da devolução ao fornecedor. A prévia de custo vem do servidor (dry-run do mesmo
// cálculo); aqui só se monta a copy exata do UI-SPEC e se valida o que o usuário digita. O servidor revalida tudo.
// A quantidade é FÍSICA (unidade de estoque da matéria-prima), com até 4 casas.
import { interpretarNumeroPtBr, mensagemNumeroAmbiguo } from '@pieltec/contracts';
import { formatarQuantidade } from '../vendas/formatacao';
import { formatarCustoUnitario } from './conferencia';
import { interpretarErroApi } from '../vendas/erro-api';

/** Mesmo limite do DTO do servidor (motivo da auditoria). */
export const MOTIVO_ESTORNO_MAXIMO = 500;
export const CASAS_QUANTIDADE_ESTORNO = 4;

export const MENSAGEM_MOTIVO_OBRIGATORIO = 'Informe o motivo.';
export const MENSAGEM_FALHA_ESTORNO = 'Não foi possível confirmar a operação. Confira a situação da nota antes de tentar de novo, pois ela pode ter sido registrada.';
export const MENSAGEM_FALHA_PREVIA = 'Não foi possível calcular a prévia do custo. Feche e tente novamente.';

function movimentos(afetados: number, singular: string, plural: string): string {
  return `${afetados} ${afetados === 1 ? singular : plural}`;
}

/** Confirmação destrutiva de "Estornar entrada" (UI-SPEC). */
export function textoConfirmacaoEstorno(quantidade: number, unidade: string, afetados: number): string {
  return `Estornar esta entrada remove ${formatarQuantidade(quantidade)} ${unidade} do estoque e recalcula o custo médio de ${movimentos(afetados, 'movimento posterior', 'movimentos posteriores')} como se ela nunca tivesse existido. O motivo fica registrado na auditoria. Confirmar estorno?`;
}

/** Prévia de custo: "Custo médio atual R$ a/un → R$ b/un. N movimento(s) posterior(es) serão recalculados." Sem custo = "sem custo médio". */
export function textoPrevia(atual: number | null, novo: number | null, unidade: string, afetados: number): string {
  const lado = (valor: number | null): string => (valor === null ? 'sem custo médio' : `${formatarCustoUnitario(valor)}/${unidade}`);
  const recalculo =
    afetados === 1 ? '1 movimento posterior será recalculado.' : `${afetados} movimentos posteriores serão recalculados.`;
  return `Custo médio atual ${lado(atual)} → ${lado(novo)}. ${recalculo}`;
}

/** Estorno impossível por saldo (UI-SPEC § Estorno impossível). */
export function textoEstornoImpossivel(codigo: string, local: string, saldo: number, unidade: string): string {
  return `Não é possível estornar: o saldo de ${codigo} em ${local} é menor que a quantidade da entrada (${formatarQuantidade(saldo)} ${unidade}). Registre a saída correspondente antes.`;
}

export function textoSucessoEstorno(afetados: number): string {
  return `Entrada estornada. O custo médio de ${movimentos(afetados, 'movimento', 'movimentos')} foi recalculado.`;
}

export function textoSucessoDevolucao(afetados: number): string {
  return `Devolução ao fornecedor registrada. O custo médio de ${movimentos(afetados, 'movimento', 'movimentos')} foi recalculado.`;
}

export type ModoDevolucao = 'TOTAL' | 'PARCIAL';

export interface ValidacaoDevolucao {
  /** Quantidade física a enviar; null = devolver todo o restante (modo Total). */
  readonly quantidade: number | null;
  readonly erroQuantidade: string | null;
  readonly erroMotivo: string | null;
  /** Só é true com motivo preenchido e quantidade válida (ou modo Total). */
  readonly valido: boolean;
}

function casasDecimais(valor: number): number {
  const arredondado = Math.round(valor * 10 ** CASAS_QUANTIDADE_ESTORNO) / 10 ** CASAS_QUANTIDADE_ESTORNO;
  return Math.abs(arredondado - valor) < 1e-9 ? 0 : CASAS_QUANTIDADE_ESTORNO + 1;
}

function arredondar(valor: number): number {
  return Math.round(valor * 10 ** CASAS_QUANTIDADE_ESTORNO) / 10 ** CASAS_QUANTIDADE_ESTORNO;
}

/**
 * Valida a devolução ao fornecedor. Parcial: maior que zero, até 4 casas e no máximo o disponível; o texto é lido pelo
 * parser pt-BR estrito ("1.234" é ambíguo e recusado em vez de adivinhado). O motivo é sempre obrigatório.
 */
export function validarDevolucao(entrada: {
  readonly modo: ModoDevolucao;
  readonly quantidade: string;
  readonly disponivel: number;
  readonly motivo: string;
  readonly unidade?: string;
}): ValidacaoDevolucao {
  const erroMotivo = entrada.motivo.trim().length === 0 ? MENSAGEM_MOTIVO_OBRIGATORIO : null;
  if (entrada.modo === 'TOTAL') {
    return { quantidade: null, erroQuantidade: null, erroMotivo, valido: erroMotivo === null };
  }
  const sufixo = entrada.unidade ? ` ${entrada.unidade}` : '';
  const lido = interpretarNumeroPtBr(entrada.quantidade);
  let erroQuantidade: string | null = null;
  let quantidade: number | null = null;
  if (lido.tipo === 'vazio') erroQuantidade = 'Informe a quantidade a devolver.';
  else if (lido.tipo === 'invalido') erroQuantidade = 'Informe a quantidade como número. Use vírgula para decimais, sem ponto de milhar.';
  else if (lido.tipo === 'ambiguo') erroQuantidade = mensagemNumeroAmbiguo(entrada.quantidade, lido);
  else if (!(lido.valor > 0)) erroQuantidade = 'A quantidade a devolver deve ser maior que zero.';
  else if (casasDecimais(lido.valor) > CASAS_QUANTIDADE_ESTORNO) erroQuantidade = `Use no máximo ${CASAS_QUANTIDADE_ESTORNO} casas decimais.`;
  else if (arredondar(lido.valor) > arredondar(entrada.disponivel)) {
    erroQuantidade = `A quantidade não pode passar do disponível para devolução (${formatarQuantidade(entrada.disponivel)}${sufixo}).`;
  } else quantidade = arredondar(lido.valor);
  return { quantidade, erroQuantidade, erroMotivo, valido: erroQuantidade === null && erroMotivo === null };
}

/** Base das rotas do item da nota (`/previa-estorno`, `/estornar` e `/devolver` são acrescentadas pelos diálogos). */
export function urlItemNota(notaId: string, itemId: string): string {
  return `/api/v1/compras/notas/${encodeURIComponent(notaId)}/itens/${encodeURIComponent(itemId)}`;
}

/** Query da prévia: sem quantidade = todo o restante. */
export function queryPreviaEstorno(quantidade: number | null): string {
  return quantidade === null ? '' : `?quantidade=${encodeURIComponent(String(quantidade))}`;
}

/** Quanto do item recebido ainda pode ser estornado/devolvido (física). */
export function disponivelParaEstorno(recebido: { readonly quantidadeEstoqueFisica: number; readonly estornadoFisico: number }): number {
  return Math.max(0, arredondar(recebido.quantidadeEstoqueFisica - recebido.estornadoFisico));
}

/**
 * Mensagem de uma falha do estorno/devolução. 409 ESTORNO_SALDO_INSUFICIENTE vira o texto do UI-SPEC (com saldo, unidade e local
 * devolvidos pelo servidor); os demais usam a mensagem do servidor ou o texto padrão.
 */
export function mensagemErroEstorno(status: number, corpo: unknown, codigoMaterial: string): string {
  const dados = (corpo && typeof corpo === 'object' ? corpo : {}) as Record<string, unknown>;
  if (status === 409 && dados.codigo === 'ESTORNO_SALDO_INSUFICIENTE' && typeof dados.saldo === 'number' && typeof dados.unidade === 'string') {
    const local = typeof dados.localizacaoNome === 'string' ? dados.localizacaoNome : 'o local da entrada';
    return textoEstornoImpossivel(codigoMaterial, local, dados.saldo, dados.unidade);
  }
  return interpretarErroApi(status, corpo, MENSAGEM_FALHA_ESTORNO).mensagem;
}
